import { Col, Row, Tooltip, Typography } from "antd-v5";
import classNames from "classnames";
import { getVocabulary } from "components/Vocabulary";
import {
  CVSS_FIELDS_NEW_UI,
  NOT_DEFINED_OPTION,
} from "constants/CVSSConstants";
import useUpdateEffect from "hooks/useUpdateEffect";
import { isEmpty, isNil } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import SubProductAPI from "restAPI/SubProductAPI";
import {
  extractCVSSVersion,
  getColorAndSeverityByScore,
  getCvssScoreFromVector,
  getStringFromModifiedCVSSObject,
  parseCVSSMetricsStringToFields,
  parseCVSSVectorStringToFields,
} from "utils/cvssUtil";

import CopyIcon from "ac-components/ac-icons/CopyIcon";
import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import ACTagColored from "ac-components/ac-tag-colored/ACTagColored";

import CVSSFormFields from "./CVSSFormFields";

export default function CVSS3_1VersionFields({
  initialCvssVector,
  onUpdate,
  onCompletenessChange,
  viewMode,
  editableModifiersFor3v1,
  resetKey,
  subProductID,
  environmentName,
}: {
  initialCvssVector: string;
  onCompletenessChange: (complete: boolean) => void;
  onUpdate: (data: {
    vector: string;
    score: number | null | undefined;
  }) => void;
  viewMode?: boolean;
  editableModifiersFor3v1?: boolean;
  resetKey: number;
  subProductID?: number;
  environmentName?: string;
}) {
  const parseFullVector = (vector: string) => {
    //sending isModified CVSS as true since 3.1 fields include base as well as modified environmental metrics
    const allMetrics = parseCVSSVectorStringToFields(vector, true);
    const base: { [key: string]: string } = {};
    const environmental: { [key: string]: string } = {};
    const baseKeys = ["AV", "AC", "PR", "UI", "S", "C", "I", "A"];

    Object.keys(allMetrics).forEach((key) => {
      if (baseKeys.includes(key)) {
        base[key] = allMetrics[key];
      } else {
        environmental[key] = allMetrics[key];
      }
    });
    return { base, environmental };
  };
  const [baseMetrics, setBaseMetrics] = useState(() => {
    if (extractCVSSVersion(initialCvssVector) === "3.1") {
      return parseFullVector(initialCvssVector).base;
    }
    return {};
  });
  const [environmentalMetrics, setEnvironmentalMetrics] = useState(() => {
    if (extractCVSSVersion(initialCvssVector) === "3.1") {
      return parseFullVector(initialCvssVector).environmental;
    }
    return {};
  });
  const [overallScore, setOverallScore] = useState<number | null>(null);
  const adaptedFields = useMemo(() => CVSS_FIELDS_NEW_UI["3.1"], []);
  const [inheritedValues, setInheritedValues] = useState<{
    [key: string]: string;
  }>();

  const [inheritedCVSSTooltip, setInheritedCVSSTooltip] = useState<
    string | undefined
  >();

  const areBaseMetricsComplete = useMemo(() => {
    const baseMetricKeys = ["AV", "AC", "PR", "UI", "S", "C", "I", "A"];
    return baseMetricKeys.every((key) => baseMetrics[key]);
  }, [baseMetrics]);

  useUpdateEffect(() => {
    if (editableModifiersFor3v1) {
      // set environmental metrics to 'Not Defined' (empty object)
      // but KEEP the baseMetrics.
      setEnvironmentalMetrics({});
    } else {
      setBaseMetrics({});
      setEnvironmentalMetrics({});
    }
  }, [resetKey, editableModifiersFor3v1]);

  const baseScoreDetails = useMemo(() => {
    if (!areBaseMetricsComplete)
      return { baseVectorString: "", baseScore: null };
    const vector = getStringFromModifiedCVSSObject(baseMetrics, "3.1");
    const score = getCvssScoreFromVector(vector);
    return { baseVectorString: vector, baseScore: score };
  }, [baseMetrics, areBaseMetricsComplete]);

  const { cvssVector, cvssScore } = useMemo(() => {
    if (!areBaseMetricsComplete) return { cvssVector: "", cvssScore: null };
    // 2. Check if any environmental metrics have been set to something other than "Not Defined".
    const environmentalValues = Object.values(environmentalMetrics);
    const hasMeaningfulOverrides =
      environmentalValues.length > 0 &&
      environmentalValues.some((val) => val !== NOT_DEFINED_OPTION.value);
    if (!hasMeaningfulOverrides) {
      return {
        cvssVector: baseScoreDetails.baseVectorString,
        cvssScore: baseScoreDetails.baseScore,
      };
    }
    const finalMetrics = { ...baseMetrics };
    adaptedFields.forEach((field) => {
      finalMetrics[field.name] = environmentalMetrics[field.name] || "X";
    });
    const vector = getStringFromModifiedCVSSObject(finalMetrics, "3.1");
    const score = getCvssScoreFromVector(vector);
    return { cvssVector: vector, cvssScore: score };
  }, [
    environmentalMetrics,
    baseMetrics,
    areBaseMetricsComplete,
    adaptedFields,
    baseScoreDetails,
  ]);

  useUpdateEffect(() => {
    onUpdate({ vector: cvssVector, score: cvssScore });
    onCompletenessChange(areBaseMetricsComplete);
  }, [
    cvssVector,
    cvssScore,
    areBaseMetricsComplete,
    onUpdate,
    onCompletenessChange,
  ]);

  const fetchOverallScore = useCallback((vector) => {
    const { promise, cancel } = FindingAPI.getOverallScore({
      vectorString: vector,
    });
    promise.then(({ data }) => {
      setOverallScore(data.overallScore);
    });
    return cancel;
  }, []);

  useEffect(() => {
    let cancel = () => {};

    if (!isEmpty(cvssVector)) {
      cancel = fetchOverallScore(cvssVector);
    } else {
      setOverallScore(null);
    }
    return cancel;
  }, [cvssVector, fetchOverallScore]);

  useEffect(() => {
    if (subProductID) {
      const { promise, cancel } = SubProductAPI.get(subProductID, {
        environment: environmentName,
      });
      promise.then((res) => {
        const productVector = res.product?.otherProperties?.modifiedVector;
        const subProductVector = res.otherProperties?.modifiedVector;
        if (productVector || subProductVector) {
          setInheritedValues(
            parseCVSSMetricsStringToFields(
              //Subproduct vector will take precedence
              subProductVector ? subProductVector : productVector,
            ),
          );
          setInheritedCVSSTooltip(
            subProductVector
              ? `Inherited from ${getVocabulary("subProduct")} ${res.name}`
              : `Inherited from ${getVocabulary("product")} ${res.product?.name}`,
          );
        }
      });
      return cancel;
    }
  }, [subProductID, environmentName]);

  const handleBaseMetricsChange = useCallback((value, fieldName) => {
    setBaseMetrics((prevState) => ({ ...prevState, [fieldName]: value }));
  }, []);

  const handleEnvironmentalMetricsChange = useCallback((value, fieldName) => {
    setEnvironmentalMetrics((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
  }, []);

  return (
    <>
      <div className="cvss-metrics-wrapper">
        <Row gutter={16}>
          <Col span={12}>
            <ScoreCard
              title="CVSS Base Score"
              score={baseScoreDetails.baseScore}
              vectorString={baseScoreDetails.baseVectorString}
              tooltipMessage="Base Score Attribute: This score is derived from the NVD"
            />
          </Col>
          <Col span={12}>
            <ScoreCard
              title="CVSS Overall Score"
              score={overallScore}
              vectorString={cvssVector}
            />
          </Col>
        </Row>
      </div>
      <CVSSFormFields
        showHeader
        fields={adaptedFields}
        cvssObject={environmentalMetrics}
        handleCvssChange={handleEnvironmentalMetricsChange}
        subtitle={
          "Environmental column shows the modified value. “Not Defined” = No modification"
        }
        showBaseMetricsColumn
        middleColumnTitle="Base Metrics"
        baseMetricsValues={baseMetrics}
        handleBaseChange={handleBaseMetricsChange}
        includeNotDefined
        viewMode={viewMode}
        editableModifiersFor3v1={editableModifiersFor3v1}
        inheritedCvssValues={inheritedValues}
        inheritedCVSSTooltip={inheritedCVSSTooltip}
      />
    </>
  );
}

const ScoreCard = ({
  title,
  vectorString,
  score,
  tooltipMessage,
}: {
  score: number | null | undefined;
  vectorString: string;
  title: string;
  tooltipMessage?: string;
}) => {
  const { color, severity } = getColorAndSeverityByScore(score || undefined);
  const needEllipsis = vectorString.length > 50;
  return (
    <div
      style={{ gap: 8, backgroundColor: "#F2F3F4" }}
      className="border-radius-6 p-x-m p-y-sm d-flex flex-column"
    >
      <div className="text-highlight">{title}</div>
      <div className="d-flex align-items-center">
        {score && (
          <Tooltip title={tooltipMessage} placement={"right"}>
            <ACTagColored
              color={color}
              label={!isNil(score) ? `${score}` : "-"}
              dot={false}
            />
          </Tooltip>
        )}
        <span className="m-l-xs color-muted">{severity}</span>
      </div>
      <div>
        <Typography.Text
          // w-100 only when vector is long, else safari 26.2 doesn't show the string at all
          className={classNames("color-muted", { "w-100": needEllipsis })}
          copyable={
            vectorString ? { text: vectorString, icon: <CopyIcon /> } : false
          }
          ellipsis={needEllipsis}
        >
          {vectorString || NOT_AVAILABLE}
        </Typography.Text>
      </div>
    </div>
  );
};
